/**
 * Converts Workflowy-style outline lines → workout payload for AddWorkoutDetails.
 *
 * Convention:
 * - depth 0: workout title; extra depth-0 lines before any exercise are workout description
 * - another depth-0 line after exercises have started begins the next workout
 * - depth 1: exercise name (placeholders like "Quad exercise" are OK)
 * - depth 2+: prescription / notes — first lines parse as sets (e.g. 3x4 rpe 8); rest → exercise.description
 * - depth 1 with "circuit" in the name: circuit block — depth 2 = sub-exercise names,
 *   depth 3+ = that sub-exercise's prescription; round count from name (e.g. "3 round circuit")
 */

const defaultSet = () => ({
  reps: "",
  weight: "",
  rpe: "",
  time: "",
  timeUnit: "sec",
  distance: "",
  degree: "",
  weightPerSide: false,
  repsPerSide: false,
  backoffPercent: "",
  backoffFromIndex: null,
});

/** Parse strings like "3x4 rpe 8", "4 x 8 @ rpe 7.5" */
export function parsePrescriptionToSets(text) {
  const raw = String(text || "").trim();
  if (!raw) return [];

  const lower = raw.toLowerCase();
  const nxm = lower.match(/(\d+)\s*[x×]\s*(\d+)/);
  if (!nxm) return [];

  const numSets = parseInt(nxm[1], 10);
  const reps = parseInt(nxm[2], 10);
  if (!Number.isFinite(numSets) || !Number.isFinite(reps) || numSets < 1) {
    return [];
  }

  const rpeMatch = lower.match(/rpe\s*(\d+(?:\.\d+)?)/);
  const rpeVal = rpeMatch ? rpeMatch[1] : "";

  const sets = [];
  for (let i = 0; i < numSets; i++) {
    sets.push({
      ...defaultSet(),
      reps: String(reps),
      rpe: rpeVal,
    });
  }
  return sets;
}

export function isCircuitExerciseName(name) {
  return /\bcircuit\b/i.test(String(name || ""));
}

function parseCircuitRoundsFromName(name) {
  const lower = String(name || "").toLowerCase();
  const m = lower.match(/(\d+)\s*(?:round|rounds|rd|wk)\b/);
  if (m) {
    const n = parseInt(m[1], 10);
    if (Number.isFinite(n) && n >= 1) return n;
  }
  return 1;
}

/** Comma- or slash-separated names after ":" or "-" in the parent line. */
function parseCircuitNamesFromParentLine(name) {
  const raw = String(name || "").trim();
  const tail = raw.split(/[:–—-]/).slice(1).join("-").trim();
  const segment = tail || raw;
  const stripped = segment
    .replace(/\bcircuit\b/gi, "")
    .replace(/\d+\s*(?:round|rounds|rd)\b/gi, "")
    .trim();
  if (!stripped) return [];

  return stripped
    .split(/\s*(?:,|\/|&|\band\b)\s*/i)
    .map((s) => s.trim())
    .filter(Boolean);
}

function buildExercise(name, prescriptionLines) {
  const lines = (prescriptionLines || []).map((l) => String(l).trim()).filter(Boolean);
  const setLines = [];
  const noteLines = [];
  for (const line of lines) {
    if (parsePrescriptionToSets(line).length > 0) setLines.push(line);
    else noteLines.push(line);
  }

  let sets = [];
  for (const line of setLines) {
    const parsed = parsePrescriptionToSets(line);
    if (parsed.length) sets = sets.concat(parsed);
  }
  if (sets.length === 0) {
    sets = [{ ...defaultSet() }];
  }

  return {
    name: String(name || "").trim() || "Exercise",
    video: "",
    restTimeMinutes: "",
    restTimeSeconds: "",
    weightUnit: "lbs",
    exerciseType: "single",
    comments: [],
    fields: ["REPS", "WEIGHT", "RPE"],
    sets,
    description: noteLines.join("\n"),
  };
}

function circuitSubExerciseFromLines(name, prescriptionLines) {
  const built = buildExercise(name, prescriptionLines);
  return {
    name: built.name,
    description: built.description,
    video: built.video,
    fields: built.fields,
    sets: built.sets,
  };
}

function cloneCircuitSubExercise(ex) {
  return {
    name: ex.name,
    description: ex.description || "",
    video: ex.video || "",
    fields: [...(ex.fields || ["REPS", "WEIGHT", "RPE"])],
    sets: (ex.sets || []).map((s) => ({ ...s })),
  };
}

function buildCircuitExercise(parentName, childRows) {
  const displayName = String(parentName || "").trim() || "Circuit";
  const rounds = parseCircuitRoundsFromName(parentName);
  const subExercises = [];
  let i = 0;

  while (i < childRows.length) {
    const row = childRows[i];
    if (row.depth !== 2) {
      i++;
      continue;
    }

    const text = String(row.text || "").trim();
    if (!text) {
      i++;
      continue;
    }

    if (parsePrescriptionToSets(text).length > 0 && subExercises.length === 0) {
      subExercises.push(circuitSubExerciseFromLines(displayName, [text]));
      i++;
      continue;
    }

    const presc = [];
    i++;
    while (i < childRows.length && childRows[i].depth >= 3) {
      presc.push(childRows[i].text);
      i++;
    }
    subExercises.push(circuitSubExerciseFromLines(text, presc));
  }

  if (subExercises.length === 0) {
    const fromParent = parseCircuitNamesFromParentLine(parentName);
    if (fromParent.length > 0) {
      for (const n of fromParent) {
        subExercises.push(circuitSubExerciseFromLines(n, []));
      }
    } else {
      subExercises.push(circuitSubExerciseFromLines(displayName, []));
    }
  }

  const roundTemplate = subExercises.map(cloneCircuitSubExercise);
  const circuit = Array.from({ length: rounds }, () =>
    roundTemplate.map(cloneCircuitSubExercise)
  );

  const base = buildExercise(displayName, []);
  return {
    ...base,
    name: displayName,
    exerciseType: "circuit",
    rounds: circuit.length,
    circuit,
    sets: [],
  };
}

function cleanOutlineLines(lines) {
  return (lines || []).map((l) => ({
    ...l,
    text: String(l.text || ""),
    depth: Math.max(0, Math.min(12, Number(l.depth) || 0)),
  }));
}

/** Split outline into one group per workout (each depth-0 title after exercises = new workout). */
export function splitOutlineIntoWorkoutBlocks(lines) {
  const cleaned = cleanOutlineLines(lines);
  if (cleaned.length === 0) return [];

  const groups = [];
  let current = [];
  let hasExercisesInBlock = false;

  for (const row of cleaned) {
    if (row.depth === 0 && current.length > 0 && hasExercisesInBlock) {
      groups.push(current);
      current = [row];
      hasExercisesInBlock = false;
      continue;
    }
    if (row.depth >= 1) hasExercisesInBlock = true;
    current.push(row);
  }

  if (current.length > 0) groups.push(current);
  return groups;
}

function parseWorkoutBlock(blockLines) {
  const cleaned = cleanOutlineLines(blockLines);
  if (cleaned.length === 0) {
    return {
      name: "Workout",
      description: "",
      exercises: [],
    };
  }

  let i = 0;
  let workoutName = "Workout";
  const desc0 = [];

  while (i < cleaned.length && cleaned[i].depth === 0) {
    if (i === 0) workoutName = cleaned[i].text.trim() || workoutName;
    else desc0.push(cleaned[i].text);
    i++;
  }

  const exercises = [];

  while (i < cleaned.length) {
    const row = cleaned[i];
    if (row.depth <= 0) {
      desc0.push(row.text);
      i++;
      continue;
    }
    if (row.depth === 1) {
      const exName = row.text;
      i++;
      const childRows = [];
      while (i < cleaned.length && cleaned[i].depth > 1) {
        childRows.push(cleaned[i]);
        i++;
      }

      if (isCircuitExerciseName(exName)) {
        exercises.push(buildCircuitExercise(exName, childRows));
      } else {
        const presc = childRows.map((r) => r.text);
        exercises.push(buildExercise(exName, presc));
      }
    } else {
      desc0.push(row.text);
      i++;
    }
  }

  return {
    name: workoutName,
    description: desc0.filter(Boolean).join("\n").trim(),
    exercises,
  };
}

/**
 * @param {{ id: string, depth: number, text: string }[]} lines — document order
 * @returns {Array<{ name: string, description: string, exercises: object[] }>}
 */
export function outlineToWorkouts(lines) {
  const blocks = splitOutlineIntoWorkoutBlocks(lines);
  if (blocks.length === 0) {
    return [
      {
        name: "Workout",
        description: "",
        exercises: [],
      },
    ];
  }
  return blocks.map(parseWorkoutBlock);
}

/** First workout from the outline (same as outlineToWorkouts(lines)[0]). */
export function outlineToWorkout(lines) {
  return outlineToWorkouts(lines)[0];
}
